import { Copy, Loader2, Trash2 } from "lucide-react";
import { ImageOverlay, ImageFilter } from "../models/Post";
import { ImageFilterPicker } from "./ImageFilterPicker";
import { useAuth } from "../contexts/AuthContext";
import { ImageSourceButton } from "./ImageSourceButton";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Slider } from "./ui/slider";
import type {
  BrandColorShortcut,
  BrandKitColorPickerContext,
} from "./BrandColorShortcuts";

const ADMIN_EMAILS = [
  "lu@wavegen.ai",
  "lulu.li1989@gmail.com",
  "experisy@gmail.com",
  "lululihq@gmail.com",
];

interface ImageOverlayFormatControlsProps {
  imageOverlay: ImageOverlay;
  onChange: (updatedOverlay: ImageOverlay) => void;
  onDelete: (id: string) => void;
  onDuplicate: (id: string) => void;
  showDeleteButton?: boolean;
  showDuplicateButton?: boolean;
  onChangeImageUpload?: (id: string) => void;
  onBrowseLibrary?: (id: string) => void;
  previewUrl?: string | null;
  loading?: boolean;
  onFillColorChange?: (id: string, color: string) => void;
  brandColors?: BrandColorShortcut[];
  brandKitContext?: BrandKitColorPickerContext;
}

export function ImageOverlayFormatControls({
  imageOverlay,
  onChange,
  onDelete,
  onDuplicate,
  showDeleteButton = true,
  showDuplicateButton = true,
  onChangeImageUpload,
  onBrowseLibrary,
  previewUrl,
  loading,
  onFillColorChange,
  brandColors,
  brandKitContext,
}: ImageOverlayFormatControlsProps) {
  const { currentUser } = useAuth();
  const isAdmin = ADMIN_EMAILS.includes(currentUser?.email || "");

  const handleOpacityChange = (value: number[]) => {
    const updatedOverlay = { ...imageOverlay, opacity: value[0] };
    onChange(updatedOverlay);
  };

  const handleBorderRadiusChange = (value: number[]) => {
    const updatedOverlay = { ...imageOverlay, borderRadius: value[0] };
    onChange(updatedOverlay);
  };

  const handleRotationChange = (value: number[]) => {
    const updatedOverlay = { ...imageOverlay, rotation: value[0] };
    onChange(updatedOverlay);
  };

  const handleFilterChange = (filter: ImageFilter | undefined) => {
    const next: ImageOverlay = { ...imageOverlay };
    if (filter) next.filter = filter;
    else delete next.filter;
    onChange(next);
  };

  const activeFilter: ImageFilter | null = imageOverlay.filter ?? null;

  const showChangeButton = onChangeImageUpload || onBrowseLibrary;

  return (
    <div className="space-y-6">
      {showChangeButton && (
        <div className="space-y-2 pb-4 border-b">
          <Label>Image Source</Label>
          {previewUrl ? (
            <div className="w-full h-32 rounded-md bg-slate-100 flex items-center justify-center overflow-hidden">
              <img
                src={previewUrl}
                alt="Overlay preview"
                className="h-full w-full object-contain"
              />
            </div>
          ) : (loading || imageOverlay.filePath) ? (
            <div className="w-full h-32 rounded-md bg-muted flex items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : null}
          <ImageSourceButton
            label="Change Image"
            onUpload={() => onChangeImageUpload?.(imageOverlay.id)}
            onBrowseLibrary={
              onBrowseLibrary
                ? () => onBrowseLibrary(imageOverlay.id)
                : undefined
            }
            align="start"
            onChangeColor={
              onFillColorChange
                ? (color) => onFillColorChange(imageOverlay.id, color)
                : undefined
            }
            currentColor={imageOverlay.sourceColor || "#000000"}
            brandColors={brandColors}
            brandKitContext={brandKitContext}
          />
        </div>
      )}
      <div className="space-y-2">
        <Label>Image Fit</Label>
        <div className="flex rounded-md border overflow-hidden">
          <button
            className={`flex-1 px-3 py-1.5 text-sm font-medium transition-colors ${
              (imageOverlay.objectFit ?? "cover") === "cover"
                ? "bg-primary text-primary-foreground"
                : "bg-background hover:bg-muted"
            }`}
            onClick={() =>
              onChange({
                ...imageOverlay,
                objectFit: "cover",
                objectPosition: imageOverlay.objectPosition ?? { x: 50, y: 50 },
              })
            }
          >
            Fill
          </button>
          <button
            className={`flex-1 px-3 py-1.5 text-sm font-medium transition-colors border-l ${
              imageOverlay.objectFit === "contain"
                ? "bg-primary text-primary-foreground"
                : "bg-background hover:bg-muted"
            }`}
            onClick={() => onChange({ ...imageOverlay, objectFit: "contain" })}
          >
            Fit
          </button>
        </div>
      </div>
      {!imageOverlay.sourceColor && (
        <ImageFilterPicker
          previewUrl={previewUrl}
          value={activeFilter}
          onChange={handleFilterChange}
        />
      )}
      <div className="space-y-2">
        <div className="flex justify-between">
          <Label>Opacity</Label>
          <span className="text-sm text-muted-foreground">
            {imageOverlay.opacity}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">0</span>
          <Slider
            value={[imageOverlay.opacity]}
            onValueChange={handleOpacityChange}
            max={100}
            step={1}
          />
          <span className="text-xs text-muted-foreground">100</span>
        </div>
      </div>
      <div className="space-y-2">
        <div className="flex justify-between">
          <Label>Corner Rounding</Label>
          <span className="text-sm text-muted-foreground">
            {imageOverlay.borderRadius || 0}px
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">0</span>
          <Slider
            value={[imageOverlay.borderRadius || 0]}
            onValueChange={handleBorderRadiusChange}
            max={500}
            step={1}
          />
          <span className="text-xs text-muted-foreground">500</span>
        </div>
      </div>
      <div className="space-y-2">
        <div className="flex justify-between">
          <Label>Rotation</Label>
          <span className="text-sm text-muted-foreground">
            {imageOverlay.rotation || 0}°
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">-180</span>
          <Slider
            value={[imageOverlay.rotation || 0]}
            onValueChange={handleRotationChange}
            max={180}
            min={-180}
            step={1}
          />
          <span className="text-xs text-muted-foreground">180</span>
        </div>
      </div>

      {isAdmin && (
        <div className="space-y-2 pt-4 border-t">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={imageOverlay.isTemplateAsset || false}
              onChange={(e) =>
                onChange({
                  ...imageOverlay,
                  isTemplateAsset: e.target.checked || undefined,
                })
              }
              className="rounded border-gray-300"
            />
            <span className="text-sm">Template asset (static)</span>
          </label>
        </div>
      )}

      {(showDuplicateButton || showDeleteButton) && (
        <div className="space-y-2 pt-4 border-t">
          {showDuplicateButton && (
            <Button
              variant="outline"
              className="w-full flex items-center justify-center gap-2"
              onClick={() => onDuplicate(imageOverlay.id)}
            >
              <Copy size={16} />
              Duplicate
            </Button>
          )}
          {showDeleteButton && (
            <Button
              variant="outline"
              className="w-full flex items-center justify-center gap-2 border-red-500 text-red-500 hover:bg-red-50 hover:text-red-700 focus-visible:ring-red-400"
              onClick={() => onDelete(imageOverlay.id)}
            >
              <Trash2 size={16} />
              Delete
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
